import type { SvgIconComponent } from '@mui/icons-material';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';
import {
  Box,
  Container,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import blueskyIcon from '../../assets/landing/img/bluesky.svg';
import pfmjLogo from '../../assets/pjmf__horizontal-white-transparent.svg';
import { CONTACT_EMAIL, SOCIAL_LINKS } from '../../common/constants';
import { landingTheme } from './theme';

const resourceLinks = [
  {
    label: 'Products and Services',
    href: 'https://www.mcgovern.org/our-work/data-solutions/',
  },
  {
    label: 'Funder Resources',
    href: 'https://learn.mcgovern.org/AI-Philanthropy-26a70021ed8c8073bcc1e24d4ce8efc4',
  },
  { label: 'Careers', href: 'https://www.mcgovern.org/about/careers/' },
  { label: 'Learning Hub', href: 'https://learn.mcgovern.org/' },
  { label: 'Blog', href: 'https://medium.com/patrick-j-mcgovern-foundation' },
];

const footerLinks = [
  { label: 'Terms', href: 'https://fdd-public.s3.amazonaws.com/tou_v1.0.pdf' },
  {
    label: 'Privacy',
    href: 'https://app.getguru.com/card/TjREnMrc/The-Patrick-J-McGovern-Foundation-Privacy-Policy-',
  },
  { label: 'Support', href: `mailto:${CONTACT_EMAIL}` },
];

const socialIcons: Array<{
  icon?: SvgIconComponent;
  imgSrc?: string;
  href: string;
  label: string;
}> = [
  { icon: LinkedInIcon, href: SOCIAL_LINKS.linkedin, label: 'LinkedIn' },
  { icon: InstagramIcon, href: SOCIAL_LINKS.instagram, label: 'Instagram' },
  { icon: XIcon, href: SOCIAL_LINKS.x, label: 'X (Twitter)' },
  { imgSrc: blueskyIcon, href: SOCIAL_LINKS.bsky, label: 'Bluesky' },
  { icon: YouTubeIcon, href: SOCIAL_LINKS.youtube, label: 'YouTube' },
];

export default function FooterSection() {
  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: landingTheme.colors.darkTeal,
        color: 'white',
        pt: 8,
        pb: 4,
        position: 'relative',
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Logo & LinkedIn */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                borderRight: {
                  xs: 'none',
                  md: '1px solid rgba(255,255,255,0.1)',
                },
                pr: { xs: 0, md: 4 },
                height: '100%',
              }}
            >
              <Box
                component="img"
                src={pfmjLogo}
                alt="Patrick J. McGovern Foundation Logo"
                sx={{ height: 80, mb: 3 }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  color="grey.400"
                  sx={{ textAlign: 'center' }}
                >
                  Stay connected
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {socialIcons.map(({ icon: Icon, imgSrc, href, label }) => (
                    <IconButton
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      sx={{
                        color: 'grey.400',
                        border: '1px solid rgba(255,255,255,0.2)',
                        width: 48,
                        height: 48,
                        '&:hover': {
                          color: landingTheme.colors.gold,
                          borderColor: landingTheme.colors.gold,
                          backgroundColor: 'rgba(255,199,25,0.1)',
                        },
                      }}
                    >
                      {Icon ? (
                        <Icon sx={{ fontSize: 28 }} />
                      ) : (
                        <Box
                          component="img"
                          src={imgSrc}
                          alt={label}
                          sx={{
                            width: 22,
                            height: 'auto',
                            display: 'block',
                            filter: 'grayscale(1) brightness(1.5)',
                            transition: 'filter 0.2s',
                            '.MuiIconButton-root:hover &': {
                              filter:
                                'brightness(0) saturate(100%) invert(78%) sepia(85%) saturate(1392%) hue-rotate(359deg) brightness(102%) contrast(101%)',
                            },
                          }}
                        />
                      )}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Resources */}
          <Grid size={{ xs: 12, md: 2 }}>
            <Typography
              variant="h6"
              color="white"
              sx={{ fontWeight: 600, mb: 3 }}
            >
              Resources
            </Typography>
            <Stack spacing={1.5}>
              {resourceLinks.map((link, index) => (
                <Link
                  key={index}
                  href={link.href}
                  target={link.href.startsWith('http') ? '_blank' : undefined}
                  rel={
                    link.href.startsWith('http')
                      ? 'noopener noreferrer'
                      : undefined
                  }
                  sx={{
                    color: 'grey.400',
                    textDecoration: 'none',
                    display: 'block',
                    '&:hover': { color: landingTheme.colors.gold },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* About Us */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h6"
              color="white"
              sx={{ fontWeight: 600, mb: 3 }}
            >
              About Us
            </Typography>
            <Typography
              variant="body2"
              color="grey.400"
              sx={{ maxWidth: '600px' }}
            >
              The Patrick J. McGovern Foundation (PJMF) is a philanthropic
              organization dedicated to advancing artificial intelligence and
              data science solutions to create a thriving, equitable, and
              sustainable future for all. PJMF works in partnership with public,
              private and social institutions to drive progress on our most
              pressing challenges, including digital health, climate change,
              broad digital access, and data maturity in the social sector.
            </Typography>
          </Grid>
        </Grid>

        {/* Footer Bottom */}
        <Box
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            pt: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          {footerLinks.map((link, index) => (
            <Link
              key={index}
              href={link.href}
              sx={{
                color: 'grey.500',
                textDecoration: 'none',
                fontSize: '0.875rem',
                '&:hover': { color: landingTheme.colors.gold },
              }}
            >
              {link.label}
            </Link>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
